/**
 * frontend/src/pages/valuation/_IndustryFactsSection.tsx
 *
 * VAL-INDUSTRY-FACTS-1C — the "Industry facts" section of the Company step.
 * Fields, labels and order come from the server. Facts kept from another
 * industry are a notice only, never inputs. Record-only: never scored.
 */
import { Input } from '@/components/ui/input'
import type { IndustryFactField, IndustryFacts } from '@/types/valuationCompany'
import { groupTotals, type FactsState } from './_industryFacts.ts'

interface Props {
  fields: IndustryFactField[]
  setLabel: string | null
  current: string
  kept: Record<string, IndustryFacts> | null
  setLabels: Record<string, string>
  state: FactsState
  onChange: (key: string, value: string) => void
  loadError: string | null
}

const SELECT_CLASS = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm'

function FactInput({ field, value, onChange }: { field: IndustryFactField; value: string; onChange: (v: string) => void }) {
  const id = `fact-${field.key}`
  let control
  if (field.type === 'yesno') {
    control = (
      <select id={id} className={SELECT_CLASS} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Not stated</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    )
  } else {
    const type = field.type === 'date' ? 'date' : field.type === 'text' ? undefined : 'number'
    control = <Input id={id} type={type} value={value} onChange={e => onChange(e.target.value)} />
  }
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-900">{field.label}</label>
      {control}
    </div>
  )
}

export function IndustryFacts({ fields, setLabel, current, kept, setLabels, state, onChange, loadError }: Props) {
  const totals = groupTotals(fields, state)
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{setLabel ? `Industry facts — ${setLabel}` : 'Industry facts'}</h2>
        <p className="text-sm text-gray-500">Record-only: saved with the signed record, never scored.</p>
      </div>
      {loadError && <p className="text-sm font-medium text-red-600">{`Industry facts could not be loaded: ${loadError}`}</p>}
      {!loadError && fields.length === 0 && <p className="text-sm text-gray-500">No industry facts for this industry.</p>}
      {fields.map(f => (
        <FactInput key={f.key} field={f} value={state[f.key] ?? ''} onChange={v => onChange(f.key, v)} />
      ))}
      {Object.entries(totals).map(([group, total]) => (
        <p key={group} className={total > 100 ? 'text-sm font-medium text-red-600' : 'text-sm text-gray-500'}>
          {`${group.charAt(0).toUpperCase()}${group.slice(1)} total ${total}%`}
        </p>
      ))}
      {Object.keys(kept ?? {}).map(key => (
        <p key={key} className="text-sm text-gray-500">
          {`Facts kept from ${setLabels[key] ?? key} — not used while the industry is ${current}. They come back if the industry is set back.`}
        </p>
      ))}
    </section>
  )
}
